import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, formatApiError } from '../lib/api'
import type { BillingUsage, OrganizationDetail } from '../lib/types'
import { Button } from './ui/Button'
import { formatDate } from '../lib/format'

/** Opens Paystack's hosted "change card" page for the org's subscription. */
export function UpdateCardButton({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const [error, setError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/v1/organizations/me/billing/update-card'),
    onSuccess: (data) => {
      window.location.href = data.url
    },
    onError: (err) => setError(formatApiError(err, 'Could not open the card update page')),
  })
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button
        size={size}
        variant="primary"
        data-testid="update-card"
        onClick={() => mutation.mutate()}
        isLoading={mutation.isPending}
      >
        Update card
      </Button>
      {error && <span className="text-sm text-danger-600">{error}</span>}
    </span>
  )
}

const longDate = (iso: string) =>
  formatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * Shown on every app page (AppShell): the trial countdown/cap, and a failed
 * renewal. Renders nothing for an org that is simply paid up. Shares the
 * ['organization'] and ['billing-usage'] queries with Settings.
 */
export function BillingNotice() {
  const { data: org } = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.get<OrganizationDetail>('/api/v1/organizations/me'),
  })
  const { data: usage } = useQuery({
    queryKey: ['billing-usage'],
    queryFn: () => api.get<BillingUsage>('/api/v1/organizations/me/billing/usage'),
  })

  if (org?.billing_status === 'past_due' && org.payment_failed_at) {
    const graceEnds = org.payment_grace_ends_at ? new Date(org.payment_grace_ends_at) : null
    const paused = graceEnds !== null && graceEnds.getTime() <= Date.now()
    return (
      <div
        data-testid="payment-failed-banner"
        className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700"
      >
        <span>
          <span className="font-medium">
            {paused ? 'Your reminders are paused.' : "Your last payment did not go through."}
          </span>{' '}
          {paused
            ? 'Update your card and they resume straight away.'
            : `Update your card${
                org.payment_grace_ends_at ? ` by ${longDate(org.payment_grace_ends_at)}` : ''
              } to keep your reminders running.`}
        </span>
        <UpdateCardButton />
      </div>
    )
  }

  const trial = usage?.plan_usage?.trial
  if (!trial) return null
  const over = trial.expired || trial.cap_reached
  return (
    <div
      data-testid="trial-banner"
      className={
        'mb-4 rounded-lg border px-4 py-3 text-sm ' +
        (over
          ? 'border-danger-200 bg-danger-50 text-danger-700'
          : 'border-brand-200 bg-brand-50 text-brand-800')
      }
    >
      {over ? (
        <>
          <span className="font-medium">Your free trial has ended.</span>{' '}
          {trial.expired
            ? 'Your 14 days are over'
            : `You used all ${trial.message_cap} trial messages`}
          {' — '}set up billing to keep sending reminders.{' '}
          <Link to="/register/billing" className="rl-tap-inline font-medium underline">
            Set up billing
          </Link>
        </>
      ) : (
        <>
          <span className="font-medium">Free trial:</span> {trial.days_left} day
          {trial.days_left === 1 ? '' : 's'} left, {trial.messages_used} of {trial.message_cap}{' '}
          trial messages used. No card needed until you're ready.{' '}
          <Link to="/register/billing" className="rl-tap-inline font-medium underline">
            Choose a plan
          </Link>
        </>
      )}
    </div>
  )
}
